import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/MainLayout';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import { Plus, Search, FileText, Trash2, Eye } from 'lucide-react';
import api from '../../lib/api';
import type { Contract, ContractListResponse } from '../../types/contract';
import {
  formatPrice,
  CONTRACT_STATUS_LABELS,
  CONTRACT_STATUS_COLORS
} from '../../types/contract';
import { ContractDialog } from './ContractDialog';

export default function Contracts() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch contracts
  const { data, isLoading } = useQuery<ContractListResponse>({
    queryKey: ['contracts', page, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', '50');
      if (search) params.append('search', search);

      const response = await api.get(`/contracts?${params.toString()}`);
      return response.data;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/contracts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast({
        title: 'Vertrag gelöscht',
        description: 'Der Vertrag wurde erfolgreich gelöscht.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description:
          (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Der Vertrag konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    },
  });

  const handleView = (contract: Contract) => {
    setEditingContract(contract);
    setDialogOpen(true);
  };

  const handleDelete = (contract: Contract) => {
    if (
      window.confirm(
        `Möchten Sie den Vertrag "${contract.contract_number}" wirklich löschen?`
      )
    ) {
      deleteMutation.mutate(contract.id);
    }
  };

  const getStatusBadge = (status: Contract['status']) => {
    return (
      <span
        className={`px-2 py-1 rounded-pill text-xs font-medium ${
          CONTRACT_STATUS_COLORS[status]
        }`}
      >
        {CONTRACT_STATUS_LABELS[status]}
      </span>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <FileText className="h-7 w-7" />
              Verträge
            </h1>
            <p className="text-muted-foreground mt-1">
              Verwalten Sie Ihre Mietverträge
            </p>
          </div>
          <Button
            onClick={() => {
              setEditingContract(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Neuer Vertrag
          </Button>
        </div>

        {/* Search */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display text-lg">Suche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suchen nach Vertragsnummer..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display">Verträge{data ? ` (${data.total})` : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 px-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : !data || data.contracts.length === 0 ? (
            <div className="text-center py-12 px-4">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Verträge gefunden</h3>
              <p className="text-muted-foreground mb-4">
                {search
                  ? 'Keine Verträge entsprechen Ihrer Suche.'
                  : 'Fügen Sie Ihren ersten Vertrag hinzu.'}
              </p>
              {!search && (
                <Button
                  onClick={() => {
                    setEditingContract(null);
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ersten Vertrag erstellen
                </Button>
              )}
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Vertragsnummer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Kunde
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Fahrzeug
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Kilometer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Endsumme
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.contracts.map((contract) => (
                    <tr
                      key={contract.id}
                      className="hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{contract.contract_number}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(contract.created_at).toLocaleDateString('de-DE')}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {contract.customer_name || `Kunde #${contract.customer_id}`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {contract.vehicle_name || `Fahrzeug #${contract.vehicle_id}`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {contract.mileage_driven
                            ? `${contract.mileage_driven.toLocaleString('de-DE')} km`
                            : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium">
                          {contract.final_total_cents
                            ? formatPrice(contract.final_total_cents)
                            : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(contract.status)}</td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(contract)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!contract.pickup_protocol && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(contract)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {data.total_pages > 1 && (
                <div className="px-4 py-3 border-t flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Seite {data.page} von {data.total_pages} • {data.total}{' '}
                    Verträge gesamt
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Zurück
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === data.total_pages}
                      onClick={() => setPage(page + 1)}
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          </CardContent>
        </Card>
      </div>

      {/* Create/View Dialog */}
      <ContractDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contract={editingContract}
        onSuccess={() => {
          setDialogOpen(false);
          setEditingContract(null);
          queryClient.invalidateQueries({ queryKey: ['contracts'] });
        }}
      />
    </MainLayout>
  );
}
